import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { updateFormInfo } from "../../redux/slice/formUpdateSlice";
import {
  hasPermission,
  platformUser,
  PRODUCT_UPDATE,
} from "../../network/service/PlatformAuth";
import {
  cancelAutoFillBatch,
  getAutoFillBatch,
  getWorklist,
  startAutoFillBatch,
} from "../../network/service/NutritionService";
import { SOURCE_NAMES } from "../products/nutrition/nutrition-model";
import PlatformSignInNotice from "../products/nutrition/platform-sign-in-notice";
import { gradeName } from "../products/nutrition/grade-names";
import "../products/nutrition/nutrition-panel.css";

const TABS = [
  ["", "Needs work"],
  ["NO_DATA", "No nutrition data"],
  ["SUSPECT", "Values look wrong"],
  ["CONFLICT", "Sources disagree"],
  ["UNRATED", "Missing facts"],
  ["REVIEW", "Auto-filled – please check"],
  ["RATED", "Graded"],
];

/** [value, label, short name on a row]; counts come from the worklist's `forms`. */
const FORMS = [
  ["PACKAGED", "Packaged", "Packaged"],
  ["LOOSE_OR_FRESH", "Not packaged (loose or fresh)", null],
  ["LOOSE", "– Loose (veg, fruit, meat, dal)", "Loose"],
  ["FRESH_PREPARED", "– Made fresh (juice, restaurant)", "Made fresh"],
  ["UNKNOWN", "Not set", null],
];
const FORM_NAMES = Object.fromEntries(FORMS.filter(([, , short]) => short).map(([value, , short]) => [value, short]));

const FACT_NAMES = { sugar: "sugars", saturatedFat: "saturated fat", sodium: "sodium" };

function problem(item) {
  if (item.status === "NO_DATA") return "No nutrition data yet";
  if (item.status === "SUSPECT") return item.issues.map((i) => i.message).join("; ");
  if (item.status === "CONFLICT") {
    return item.conflicts
      .map((c) => {
        const others = c.others.map((o) => `${SOURCE_NAMES[o.source] ?? o.source} ${o.value}`).join(", ");
        return `${c.name} ${c.current.value} ${c.unit} vs ${others}`;
      })
      .join("; ");
  }
  if (item.status === "UNRATED") {
    const facts = item.missingFacts.map((f) => FACT_NAMES[f] ?? f).join(", ");
    return item.grade && item.grade !== "UNRATED" ? `Graded on partial data: enter ${facts} from the label` : `Missing ${facts}`;
  }
  if (item.status === "REVIEW") {
    const match = item.matchConfidence ? ` (${Math.round(item.matchConfidence * 100)}% name match)` : "";
    return `Filled automatically from ${SOURCE_NAMES[item.source] ?? "a database"}${match}: check and save`;
  }
  return "";
}

/**
 * Products whose nutrition label needs an admin, most widely stocked
 * first. Opens the nutrition editor for a product.
 */
function NutritionWorklist() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = platformUser();
  const [status, setStatus] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [form, setForm] = useState("");
  const [micronutrients, setMicronutrients] = useState("");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const canEdit = !!user && hasPermission(PRODUCT_UPDATE);

  // Search as the admin types, without a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await getWorklist({ status, form, micronutrients, categoryId, q, page, limit: 25 }));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [status, form, micronutrients, categoryId, q, page]);

  useEffect(() => {
    if (canEdit) load();
  }, [canEdit, load]);

  const openEditor = (item) => {
    dispatch(
      updateFormInfo({
        displayForm: false,
        formData: {
          product: {
            id: item.productId,
            product_name: item.name,
            category_id: item.categoryId,
          },
        },
      }),
    );
    navigate("/nutrition-edit");
  };

  const count = (tab) => {
    if (!data) return "";
    const c = data.counts;
    return tab === "" ? c.NO_DATA + c.SUSPECT + c.CONFLICT + c.UNRATED + c.REVIEW : c[tab];
  };

  return (
    <main className="container-fluid dashboard">
      <div className="nutri-panel">
        <div className="nutri-header">
          <div>
            <h4>Nutrition worklist</h4>
            <p className="nutri-muted mb-0">
              Products customers can't get a health grade for yet. Most widely stocked first,
              so fixing the top rows helps the most customers.
            </p>
          </div>
        </div>

        {!user && <PlatformSignInNotice />}
        {user && !canEdit && (
          <div className="nutri-status nutri-status-warn">
            Your admin role cannot edit nutrition. Ask a super admin for catalog access.
          </div>
        )}

        {canEdit && <AutoFillPanel onFinished={load} />}

        {canEdit && (
          <>
            <div className="nutri-tabs">
              {TABS.map(([value, label]) => (
                <button
                  key={value}
                  className={`nutri-tab${status === value ? " active" : ""}`}
                  onClick={() => {
                    setStatus(value);
                    setCategoryId("");
                    setPage(1);
                  }}
                >
                  {label} <span className="nutri-tab-count">{count(value)}</span>
                </button>
              ))}
            </div>

            <div className="nutri-row nutri-filters">
              <input
                className="form-control"
                placeholder="Search product name or id"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <select
                className="form-select"
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All categories</option>
                {data?.categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.count})
                  </option>
                ))}
              </select>
              <select
                className="form-select"
                value={form}
                onChange={(e) => {
                  setForm(e.target.value);
                  setCategoryId("");
                  setPage(1);
                }}
              >
                <option value="">Packaged and not packaged</option>
                {FORMS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                    {data?.forms ? ` (${data.forms[value]})` : ""}
                  </option>
                ))}
              </select>
              <select
                className="form-select"
                value={micronutrients}
                onChange={(e) => {
                  setMicronutrients(e.target.value);
                  setCategoryId("");
                  setPage(1);
                }}
              >
                <option value="">Any vitamins &amp; minerals</option>
                <option value="MISSING">No vitamins &amp; minerals</option>
                <option value="PRESENT">Has vitamins &amp; minerals</option>
              </select>
            </div>

            {error && (
              <div className="alert alert-danger">
                {error} <button className="nutri-link" onClick={load}>Retry</button>
              </div>
            )}

            <table className="table table-sm nutri-worklist">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th title="Stores listing this product">Stores</th>
                  <th>What needs doing</th>
                  <th>Grade</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {loading && !data && (
                  <tr><td colSpan={6} className="nutri-muted">Loading…</td></tr>
                )}
                {data?.items.length === 0 && (
                  <tr><td colSpan={6} className="nutri-muted">Nothing here. Nice work!</td></tr>
                )}
                {data?.items.map((item) => (
                  <tr key={item.productId} className={loading ? "nutri-stale" : ""}>
                    <td>
                      <strong>{item.name}</strong>
                      <div className="nutri-muted">#{item.productId}</div>
                    </td>
                    <td>
                      {item.categoryName}
                      <div className="nutri-muted">{FORM_NAMES[item.productForm] ?? "Form not set"}</div>
                    </td>
                    <td>{item.storeCount}</td>
                    <td className={item.status === "SUSPECT" ? "text-danger" : ""}>{problem(item)}</td>
                    <td>
                      {item.grade && (
                        <span className="nutri-chip" style={{ background: item.color }}>
                          {item.grade === "UNRATED" ? "Not rated" : gradeName(item.grade)}
                        </span>
                      )}
                    </td>
                    <td>
                      <button className="btn btn-sm btn-purple-bg" onClick={() => openEditor(item)}>
                        Edit nutrition
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {data && data.totalPages > 1 && (
              <div className="nutri-actions">
                <button className="btn btn-white-bg" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Previous
                </button>
                <span className="nutri-muted">
                  Page {data.page} of {data.totalPages} · {data.total} products
                </span>
                <button
                  className="btn btn-white-bg"
                  disabled={page >= data.totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}

export default NutritionWorklist;

/**
 * Fills the catalog from Open Food Facts / USDA / FatSecret in the background, most
 * widely stocked products first. Name matches are saved and flagged
 * "Auto-filled – please check"; weak matches are left for an admin.
 */
function AutoFillPanel({ onFinished }) {
  const [batch, setBatch] = useState(null);
  const [error, setError] = useState("");
  const wasRunning = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const b = await getAutoFillBatch();
      setBatch(b);
      if (wasRunning.current && !b.running) onFinished();
      wasRunning.current = b.running;
    } catch (e) {
      setError(e.message);
    }
  }, [onFinished]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!batch?.running) return;
    const timer = setInterval(refresh, 3000);
    return () => clearInterval(timer);
  }, [batch?.running, refresh]);

  const start = async () => {
    setError("");
    try {
      setBatch(await startAutoFillBatch({ limit: 200 }));
      wasRunning.current = true;
    } catch (e) {
      setError(e.message);
    }
  };

  const running = batch?.running;
  const done = batch && !running && batch.finishedAt;

  return (
    <div className="nutri-autofill">
      <div className="nutri-row" style={{ alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <strong>Fill nutrition automatically</strong>
          <div className="nutri-muted">
            Looks up the next 200 products in Open Food Facts, USDA and FatSecret and saves confident matches.
            Name matches appear under “Auto-filled – please check”.
          </div>
        </div>
        {running ? (
          <button type="button" className="btn btn-white-bg" onClick={() => cancelAutoFillBatch().then(refresh)}>
            Stop
          </button>
        ) : (
          <button type="button" className="btn btn-purple-bg" onClick={start}>
            Auto-fill products
          </button>
        )}
      </div>

      {running && (
        <div className="nutri-status nutri-status-info">
          <div className="nutri-progress">
            <div style={{ width: `${batch.total ? (batch.processed * 100) / batch.total : 0}%` }} />
          </div>
          {batch.processed} of {batch.total} checked · {batch.applied} filled · {batch.suggested} need a choice ·{" "}
          {batch.noMatch} not found
          {batch.current && <div className="nutri-muted">Now: {batch.current}</div>}
        </div>
      )}
      {done && (
        <div className="nutri-status nutri-status-ok">
          Last run: {batch.applied} filled, {batch.suggested} need a choice, {batch.noMatch} not found
          {batch.errors > 0 && `, ${batch.errors} errors`} ({new Date(batch.finishedAt).toLocaleString()}).
        </div>
      )}
      {batch?.warnings?.length > 0 && <p className="nutri-muted mb-0 mt-2">Note: {batch.warnings.join("; ")}</p>}
      {error && <p className="text-danger mb-0 mt-2">{error}</p>}
    </div>
  );
}
